/** Stackable slatted poultry transport crate, ready for repeated placement in Beat 8. */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { getZone, type ZonePalette } from '@/render/palette';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const zone: ZonePalette = (() => {
  const found = getZone('fattening');
  if (!found) throw new Error('hen-crate: fattening palette missing');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`hen-crate: fattening surface ${index} missing`);
  return colour;
}

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

type BoxPiece = Readonly<{
  size: readonly [number, number, number];
  position: readonly [number, number, number];
}>;

/** Bake one material island into one mesh; the six placed crates make this a budget requirement. */
function mergedBoxes(name: string, pieces: readonly BoxPiece[], material: THREE.Material): THREE.Mesh {
  const geometries = pieces.map(({ size, position }) => {
    const geometry = new THREE.BoxGeometry(...size);
    geometry.translate(...position);
    return geometry;
  });
  const merged = mergeGeometries(geometries, false);
  for (const geometry of geometries) geometry.dispose();
  if (!merged) throw new Error(`hen-crate: failed to merge ${name}`);
  return part(merged, material, name);
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hen-crate';
  const plastic = surface('mineral', zoneSurface(3));
  const recess = surface('mineral', zoneSurface(0));

  const feet = new THREE.Group();
  feet.name = 'hen-crate-stack-feet';
  const footPieces: BoxPiece[] = [];
  for (const x of [-0.31, 0.31]) {
    for (const z of [-0.21, 0.21]) {
      footPieces.push({ size: [0.09, 0.025, 0.08], position: [x, 0.0125, z] });
    }
  }
  feet.add(mergedBoxes('hen-crate-feet', footPieces, recess));
  group.add(feet);

  const base = new THREE.Group();
  base.name = 'hen-crate-base-rim';
  const basePieces: BoxPiece[] = [
    { size: [0.75, 0.05, 0.04], position: [0, 0.05, -0.255] },
    { size: [0.75, 0.05, 0.04], position: [0, 0.05, 0.255] },
    { size: [0.04, 0.05, 0.51], position: [-0.355, 0.05, 0] },
    { size: [0.04, 0.05, 0.51], position: [0.355, 0.05, 0] },
  ];
  // Spaced floor bars make the lower opening read through the side slots rather
  // than turning this transport crate into a single opaque plastic volume.
  for (const z of [-0.16, -0.05, 0.06, 0.17]) {
    basePieces.push({ size: [0.64, 0.028, 0.026], position: [0, 0.07, z] });
  }
  base.add(mergedBoxes('hen-crate-base', basePieces, plastic));
  group.add(base);

  const walls = new THREE.Group();
  walls.name = 'hen-crate-slatted-walls';
  const wallPieces: BoxPiece[] = [];
  for (const x of [-0.355, 0.355]) {
    for (const y of [0.125, 0.195]) {
      wallPieces.push({ size: [0.04, 0.035, 0.42], position: [x, y, 0] });
    }
  }
  for (const z of [-0.255, 0.255]) {
    for (const x of [-0.22, 0, 0.22]) {
      wallPieces.push({ size: [0.04, 0.18, 0.04], position: [x, 0.16, z] });
    }
  }
  walls.add(mergedBoxes('hen-crate-walls', wallPieces, plastic));
  group.add(walls);

  const rim = new THREE.Group();
  rim.name = 'hen-crate-top-rim';
  rim.add(mergedBoxes('hen-crate-rim', [
    { size: [0.75, 0.035, 0.04], position: [0, 0.245, -0.255] },
    { size: [0.75, 0.035, 0.04], position: [0, 0.245, 0.255] },
    { size: [0.04, 0.035, 0.51], position: [-0.355, 0.245, 0] },
    { size: [0.04, 0.035, 0.51], position: [0.355, 0.245, 0] },
  ], plastic));
  group.add(rim);

  const lidPivot = new THREE.Group();
  lidPivot.name = 'hen-crate-lid-pivot';
  // The hinge line is the rear (-Z) rim. Identity is closed; the crate opener
  // can later rotate this group without knowing where its slats were authored.
  lidPivot.position.set(0, 0.27, -0.255);
  const lid = new THREE.Group();
  lid.name = 'hen-crate-lid';
  const lidPieces: BoxPiece[] = [
    { size: [0.75, 0.03, 0.04], position: [0, 0.015, 0.02] },
    { size: [0.75, 0.03, 0.04], position: [0, 0.015, 0.49] },
    { size: [0.04, 0.03, 0.51], position: [-0.355, 0.015, 0.255] },
    { size: [0.04, 0.03, 0.51], position: [0.355, 0.015, 0.255] },
  ];
  for (const z of [0.14, 0.255, 0.37]) {
    lidPieces.push({ size: [0.66, 0.024, 0.025], position: [0, 0.015, z] });
  }
  lid.add(mergedBoxes('hen-crate-lid-slats', lidPieces, plastic));
  lidPivot.add(lid);
  group.add(lidPivot);

  const interlocks = new THREE.Group();
  interlocks.name = 'hen-crate-interlock-sockets';
  const interlockPieces: BoxPiece[] = [];
  for (const x of [-0.28, 0.28]) {
    interlockPieces.push({ size: [0.07, 0.012, 0.05], position: [x, 0.294, 0] });
  }
  interlocks.add(mergedBoxes('hen-crate-interlocks', interlockPieces, recess));
  group.add(interlocks);

  return group;
}

export const henCrateAsset: AssetDefinition = {
  id: 'hen-crate',
  label: 'Hen crate',
  description: 'Low stackable plastic poultry transport crate with open slatted sides and a hinged lid.',
  footprint: { width: 0.75, height: 0.30, length: 0.55 },
  create: build,
};
